import { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import {
  Box, Paper, Typography, TextField, Button, Container, Grid, Dialog,
  DialogTitle, DialogContent, DialogActions, Divider, Alert, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';

interface FormData {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    if (user) setFormData((prev) => ({ ...prev, name: user.name, email: user.email }));
  }, [user]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showMessage = (msg: string, error = false) => {
    setMessage(msg);
    setIsError(error);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await authService.updateProfile({ name: formData.name, email: formData.email });
      setEditing(false);
      showMessage('Profile updated successfully');
    } catch (err: any) {
      showMessage(err.message || 'Failed to update profile', true);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      showMessage('Passwords do not match', true);
      return;
    }
    setLoading(true);
    try {
      await authService.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });
      setPasswordOpen(false);
      setFormData((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      showMessage('Password changed successfully');
    } catch (err: any) {
      showMessage(err.message || 'Failed to change password', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This cannot be undone.')) return;
    try {
      await authService.updateProfile({ name: '', email: '' }); // placeholder
      navigate('/login');
    } catch (err: any) {
      showMessage(err.message || 'Failed to delete account', true);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="text.secondary" gutterBottom>Please log in to access settings</Typography>
          <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>Log In</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <div>
            <Button variant="text" onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
              Back to Dashboard
            </Button>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>Account Settings</Typography>
          </div>
          <Button variant="outlined" onClick={() => setEditing(true)} startIcon={<EditIcon />} disabled={editing}>
            Edit Profile
          </Button>
        </Stack>

        {message && <Alert severity={isError ? 'error' : 'success'} sx={{ mb: 3 }}>{message}</Alert>}

        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>Profile Information</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Name" name="name"
                    value={editing ? formData.name : user.name}
                    onChange={handleChange} disabled={!editing} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Email" name="email"
                    value={editing ? formData.email : user.email}
                    onChange={handleChange} disabled={!editing} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Role: {user.role_id === 1 ? 'Admin' : 'User'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>Security</Typography>
              <Stack spacing={2}>
                <Box>
                  <Button variant="outlined" startIcon={<LockIcon />} onClick={() => setPasswordOpen(true)}>
                    Change Password
                  </Button>
                </Box>
                <Box>
                  <Button variant="outlined" color="error" onClick={handleDeleteAccount} startIcon={<DeleteIcon />}>
                    Delete Account
                  </Button>
                </Box>
              </Stack>
            </Box>

            {editing && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => setEditing(false)} sx={{ mr: 1 }}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveProfile} startIcon={<SaveIcon />} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>

      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Current Password" name="currentPassword" type="password"
              value={formData.currentPassword} onChange={handleChange} autoComplete="current-password" />
            <TextField fullWidth label="New Password" name="newPassword" type="password"
              value={formData.newPassword} onChange={handleChange} autoComplete="new-password" />
            <TextField fullWidth label="Confirm New Password" name="confirmPassword" type="password"
              value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordOpen(false)}>Cancel</Button>
          <Button onClick={handleChangePassword} color="primary" variant="contained" disabled={loading}>
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
